'use client';
import { FC, useState } from 'react';
import { Box, Typography, Stack, Paper, Tabs, Tab } from '@mui/material';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import CollaboratorsTab from '@/components/details/CollaboratorsTab';
import DetailField from '@/components/common/DetailField';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { formatDate } from '@/lib/utils/dateUtils';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { UserLink, UserLinks } from '@/components/common/UserLink';
import { PrivilegeEnum, ProjectStatusDto } from '@/api/ApiClient';
import { usePrivileges } from '@/hooks/usePrivileges';
import { getPrivilegeForEntity } from '@/types/tree';
import { useAuthStore } from '@/stores/authStore';
import { canShowProjectCollaboratorsTab } from '@/lib/models/projectUIModel';
import { formatUsJurisdictionForDisplay } from '@/lib/constants/usJurisdictionOptions';

export const selectOptions: OptionKey[] = [
  'researcherOptions',
  'facultyOptions',
  'externalUserOptions',
  'projectType',
  'projectCategory',
  'projectStatus',
  'materials',
  'synthesisTechniques',
  'users',
  'staff',
];

const ProjectDetailsReadOnly: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { getProjectData } = useTree();
  const { selectedNode } = useTreeSelection();
  const { resolveUser } = useUserResolution();
  const { isAuthorized } = usePrivileges();
  const currentUserId = useAuthStore((s) => s.currentUser?.id);
  const [tabIndex, setTabIndex] = useState(0);

  const project = selectedNode ? getProjectData(selectedNode) : undefined;

  if (!project) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading project details...
        </Typography>
      </Box>
    );
  }

  if (optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading project details...
        </Typography>
      </Box>
    );
  }

  const privilege = getPrivilegeForEntity('project', project) ?? PrivilegeEnum.UserProject;
  const canEditProject = isAuthorized(privilege, 'update');
  const showCollaboratorsTab = canShowProjectCollaboratorsTab(project, { canEditProject, currentUserId });
  const activeTabIndex = !showCollaboratorsTab && tabIndex === 1 ? 0 : tabIndex;
  const isInternalProject = !!project.type?.isInternal;

  const getMaterialName = (id: number) => {
    const material = options.materials?.find((m) => m.id === id);
    return material?.label ?? material?.name ?? id.toString();
  };

  const getTechniqueName = (id: string) => {
    return options.synthesisTechniques?.find((m) => m.id === id)?.name || id;
  };

  const getProjectStatusDisplay = (status: string | undefined) => {
    if (!status) return '';
    return options.projectStatus?.find((s) => s.key === status)?.value || status;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Tabs
        value={activeTabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        aria-label="Project detail tabs"
        sx={{ flexShrink: 0 }}
      >
        <Tab label="Details" />
        {showCollaboratorsTab ? <Tab label="Additional Collaborators" /> : null}
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {activeTabIndex === 0 ? (
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <DetailField
                label="Title"
                value={<span dangerouslySetInnerHTML={{ __html: project.title || '' }} />}
                isReadOnly={true}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="User PI"
                  value={
                    project.piUserId ? (
                      <UserLink userId={project.piUserId} displayText={resolveUser(project.piUserId)} />
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={true}
                />

                <DetailField
                  label="Project Status"
                  value={getProjectStatusDisplay(project.projectStatus)}
                  isReadOnly={true}
                />

                <DetailField label="Published" value={project.isPublished ? 'Yes' : 'No'} isReadOnly={true} />
              </Box>

              {!isInternalProject ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <DetailField
                    label="Institution at award"
                    value={project.piInstitution?.trim() || 'N/A'}
                    isReadOnly={true}
                  />
                  <DetailField
                    label="State at award"
                    value={formatUsJurisdictionForDisplay(project.piState) || project.piState?.trim() || 'N/A'}
                    isReadOnly={true}
                  />
                  <DetailField
                    label="Institution type"
                    value={
                      options.projectCategory?.find((c) => c.key === project.category)?.value ||
                      project.institutionType?.trim() ||
                      project.category ||
                      'N/A'
                    }
                    isReadOnly={true}
                  />
                </Box>
              ) : null}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Faculty"
                  value={
                    project.facultyUserIds && project.facultyUserIds.length > 0 ? (
                      <UserLinks
                        userIds={project.facultyUserIds}
                        displayTexts={project.facultyUserIds.map((id) => resolveUser(id))}
                      />
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={true}
                />

                <DetailField
                  label="Materials"
                  value={
                    project.materialIds?.map((id) => getMaterialName(id)).join(', ') ||
                    project.materials?.join(', ') ||
                    'N/A'
                  }
                  isReadOnly={true}
                />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Staff"
                  value={
                    project.staffUserIds && project.staffUserIds.length > 0 ? (
                      <UserLinks
                        userIds={project.staffUserIds}
                        displayTexts={project.staffUserIds.map((id) => resolveUser(id))}
                      />
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={true}
                />

                <DetailField
                  label="Techniques"
                  value={project.techniques?.map((id) => getTechniqueName(id)).join(', ') || 'N/A'}
                  isReadOnly={true}
                />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Start Date"
                  value={project.startDate ? formatDate(project.startDate) : 'N/A'}
                  isReadOnly={true}
                />

                <DetailField
                  label="End Date"
                  value={project.endDate ? formatDate(project.endDate) : 'N/A'}
                  isReadOnly={true}
                />
              </Box>
            </Stack>
          </Paper>
        ) : null}

        {showCollaboratorsTab && activeTabIndex === 1 ? (
          <CollaboratorsTab project={project} isReadOnly={true} onUpdateCollaborators={() => {}} />
        ) : null}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <ReadOnlyFooter
          lastChangedDate={project.lastChangedDate}
          lastChangedBy={project.lastChangedByUser || 'Unknown'}
          status={project.projectStatus || ProjectStatusDto.Pending}
        />
      </Box>
    </Box>
  );
};

export default ProjectDetailsReadOnly;
